// components/chat-reaction-picker/ChatReactionPicker.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import EmojiPicker from "../emoji/emojiPicker";
import StickerPicker from "../sticker-picker/StickerPicker";
import GifPicker from "../gif-picker/GifPicker";

import "./chat-reaction-picker.styles.scss";

const EDGE = 8;
const GAP = 8;

export default function ChatReactionPicker({
  open,
  anchorRect,
  initialTab = "emoji",
  onClose,
  onSelectEmoji,
  onSelectSticker,
  onSelectGif,
  emojisOn = true,
  stickersOn = true,
  gifsOn = true,
  gifApiKey,
  gifAttributionLogoSrc,
  renderSticker,
}) {
  const panelRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: EDGE, left: EDGE });

  const enabledTabs = useMemo(
    () =>
      [
        emojisOn && { key: "emoji", label: "Emoji" },
        stickersOn && { key: "stickers", label: "Stickers" },
        gifsOn && { key: "gifs", label: "GIFs" },
      ].filter(Boolean),
    [emojisOn, stickersOn, gifsOn],
  );

  const firstTab = enabledTabs[0]?.key || "emoji";
  const [activeTab, setActiveTab] = useState(firstTab);

  const positionPanel = useCallback(
    (reveal = false) => {
      const panel = panelRef.current;
      if (!panel || !anchorRect) return;

      const width = panel.offsetWidth || Math.min(350, window.innerWidth - 16);
      const height = panel.offsetHeight || Math.min(430, window.innerHeight - 16);

      let left = anchorRect.right - width;
      if (left < EDGE) left = EDGE;
      if (left + width > window.innerWidth - EDGE) {
        left = window.innerWidth - width - EDGE;
      }

      const roomAbove = anchorRect.top - EDGE;
      const roomBelow = window.innerHeight - anchorRect.bottom - EDGE;

      let top;
      if (roomAbove >= height + GAP || roomAbove >= roomBelow) {
        top = anchorRect.top - height - GAP;
      } else {
        top = anchorRect.bottom + GAP;
      }

      top = Math.max(
        EDGE,
        Math.min(top, window.innerHeight - height - EDGE),
      );

      setCoords({ top, left });
      if (reveal) requestAnimationFrame(() => setVisible(true));
    },
    [anchorRect],
  );

  useEffect(() => {
    if (!open) {
      setVisible(false);
      return;
    }

    const requested = enabledTabs.some((tab) => tab.key === initialTab)
      ? initialTab
      : firstTab;
    setActiveTab(requested);
    setVisible(false);
  }, [open, enabledTabs, firstTab, initialTab]);

  useLayoutEffect(() => {
    if (!open) return;
    positionPanel(true);
  }, [open, activeTab, positionPanel]);

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const handleRelayout = () => positionPanel(false);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    const handlePointerDown = (event) => {
      if (!panelRef.current?.contains(event.target)) {
        onClose?.();
      }
    };

    const resizeObserver =
      typeof ResizeObserver !== "undefined" && panel
        ? new ResizeObserver(handleRelayout)
        : null;

    resizeObserver?.observe(panel);
    window.addEventListener("resize", handleRelayout);
    window.addEventListener("scroll", handleRelayout, true);
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("touchstart", handlePointerDown, true);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", handleRelayout);
      window.removeEventListener("scroll", handleRelayout, true);
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("touchstart", handlePointerDown, true);
    };
  }, [open, onClose, positionPanel]);

  if (!open || typeof document === "undefined") return null;

  const pick = (callback, value) => {
    onClose?.();
    callback?.(value);
  };

  return createPortal(
    <div
      ref={panelRef}
      className="chatReactionPicker"
      style={{
        top: coords.top,
        left: coords.left,
        visibility: visible ? "visible" : "hidden",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
      }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {enabledTabs.length > 1 && (
        <div className="chatReactionPicker__tabs">
          {enabledTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={
                activeTab === tab.key
                  ? "chatReactionPicker__tab chatReactionPicker__tab--active"
                  : "chatReactionPicker__tab"
              }
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="chatReactionPicker__body">
        {activeTab === "emoji" && emojisOn && (
          <div className="chatReactionPicker__emoji">
            <EmojiPicker
              embedded
              onEmojiSelect={(emoji) => pick(onSelectEmoji, emoji)}
            />
          </div>
        )}

        {activeTab === "stickers" && stickersOn && (
          <StickerPicker
            renderSticker={renderSticker}
            onSelect={(key) => pick(onSelectSticker, key)}
          />
        )}

        {activeTab === "gifs" && gifsOn && (
          <GifPicker
            apiKey={gifApiKey}
            attributionLogoSrc={gifAttributionLogoSrc}
            onSelect={(gif) => pick(onSelectGif, gif)}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
